import { useTranslation } from '../hooks/useTranslation'
import { DOMAIN_BADGE_CLASSES, DOMAIN_REGISTRY } from '../lib/domainRegistry'
import { PIPELINE_BADGE_CLASSES, PIPELINE_MAP } from '../lib/pipelineRegistry'
import { TableToolbar } from './TableToolbar'
import type { ProjectListItemV2, Pagination } from '../types'

interface ProjectsTableProps {
  projects: ProjectListItemV2[]
  pagination: Pagination
  loading: boolean
  sort: string
  order: 'asc' | 'desc'
  onSort: (column: string) => void
  onPageChange: (page: number) => void
  onRowClick: (project: ProjectListItemV2) => void
  searchValue: string
  onSearchChange: (q: string) => void
}

function DomainBadge({ domain }: { domain: string | null }) {
  if (!domain) return <span className="text-text-tertiary text-caption">—</span>
  const badgeClass = DOMAIN_BADGE_CLASSES[domain as keyof typeof DOMAIN_BADGE_CLASSES] || 'bg-gray-500/15 text-gray-400'
  const config = DOMAIN_REGISTRY[domain as keyof typeof DOMAIN_REGISTRY]
  return (
    <span className={`inline-flex items-center px-[10px] py-[3px] rounded-xl text-[10px] font-semibold whitespace-nowrap ${badgeClass}`}>
      {config?.label || domain}
    </span>
  )
}

function PipelineStageBadge({ stage }: { stage: string }) {
  if (!stage) return <span className="text-text-tertiary text-caption">—</span>
  const badgeClass = PIPELINE_BADGE_CLASSES[stage as keyof typeof PIPELINE_BADGE_CLASSES] || 'bg-gray-500/15 text-gray-400'
  const config = PIPELINE_MAP[stage as keyof typeof PIPELINE_MAP]
  return (
    <span className={`inline-flex items-center px-[10px] py-[3px] rounded-sm text-[10px] font-semibold whitespace-nowrap ${badgeClass}`}>
      {config?.label || stage}
    </span>
  )
}

function ScoreDisplay({ score }: { score: number | null }) {
  if (score === null) return <span className="text-text-tertiary text-caption">—</span>

  const colorClass = score > 0.7 ? 'text-score-high' : score > 0.4 ? 'text-score-medium' : 'text-score-low'

  return (
    <span className={`font-mono text-[13px] font-semibold tabular-nums ${colorClass}`}>
      {score.toFixed(2)}
    </span>
  )
}

function VoteTally({ voteTally }: { voteTally: number }) {
  if (voteTally === 0) return <span className="text-text-tertiary text-caption">—</span>
  const sign = voteTally > 0 ? '+' : ''
  const color = voteTally > 0 ? 'text-green-400' : voteTally < 0 ? 'text-red-400' : 'text-text-secondary'
  return <span className={`text-body-sm font-medium ${color}`}>{sign}{voteTally}</span>
}

// ─── Sort header ──────────────────────────────────────────────────────────────

function SortTh({
  label,
  column,
  sort,
  order,
  onSort,
}: {
  label: string
  column: string
  sort: string
  order: 'asc' | 'desc'
  onSort: (col: string) => void
}) {
  const active = sort === column
  return (
    <th
      className="px-4 py-[10px] text-left text-[10px] font-semibold text-text-tertiary uppercase tracking-[0.5px] cursor-pointer select-none hover:text-text-secondary transition-colors bg-bg-input border-b border-border-subtle"
      onClick={() => onSort(column)}
    >
      <span className="flex items-center gap-1">
        {label}
        <span className="text-[10px]">
          {active ? (order === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </span>
    </th>
  )
}

// ─── Pagination ───────────────────────────────────────────────────────────────

function PaginationBar({
  pagination,
  onPageChange,
}: {
  pagination: Pagination
  onPageChange: (page: number) => void
}) {
  const { page, pages, total, limit } = pagination
  const start = (page - 1) * limit + 1
  const end = Math.min(page * limit, total)

  const btnClass =
    'px-3 py-1.5 text-body-sm rounded border border-border-standard hover:bg-bg-surface-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed'

  const pageNumbers = (): (number | '…')[] => {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1)
    if (page <= 4) return [1, 2, 3, 4, 5, '…', pages]
    if (page >= pages - 3) return [1, '…', pages - 4, pages - 3, pages - 2, pages - 1, pages]
    return [1, '…', page - 1, page, page + 1, '…', pages]
  }

  const { t } = useTranslation()
  return (
    <div className="flex items-center justify-between px-3 py-3 border-t border-border-subtle">
      <span className="text-caption text-text-secondary">
        {t('table.pagination', { start, end, total })}
      </span>
      <div className="flex items-center gap-1">
        <button
          className={btnClass}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label={t('table.prev_page')}
        >
          ‹
        </button>
        {pageNumbers().map((p, i) =>
          p === '…' ? (
            <span key={`ellipsis-${i}`} className="px-2 text-text-tertiary text-caption">
              …
            </span>
          ) : (
            <button
              key={p}
              className={`px-3 py-1.5 text-body-sm rounded border transition-colors ${
                p === page
                  ? 'border-primary-red text-primary-red bg-primary-red/10'
                  : 'border-border-standard text-text-secondary hover:bg-bg-surface-hover'
              }`}
              onClick={() => onPageChange(p as number)}
            >
              {p}
            </button>
          ),
        )}
        <button
          className={btnClass}
          disabled={page >= pages}
          onClick={() => onPageChange(page + 1)}
          aria-label={t('table.next_page')}
        >
          ›
        </button>
      </div>
    </div>
  )
}

export function ProjectsTable({
  projects,
  pagination,
  loading,
  sort,
  order,
  onSort,
  onPageChange,
  onRowClick,
  searchValue,
  onSearchChange,
}: ProjectsTableProps) {
  const { t } = useTranslation()
  return (
    <div className="bg-bg-surface border border-border-subtle rounded-lg overflow-hidden">
      <TableToolbar searchValue={searchValue} onSearchChange={onSearchChange} />
      <div className="overflow-x-auto">
        <table className="w-full text-text-primary">
          <thead>
            <tr>
              <SortTh label={t('table.title')} column="title" sort={sort} order={order} onSort={onSort} />
              <SortTh label={t('table.domain')} column="domain" sort={sort} order={order} onSort={onSort} />
              <SortTh label={t('table.pipeline_stage')} column="pipeline_stage" sort={sort} order={order} onSort={onSort} />
              <SortTh label={t('table.score')} column="score_composite" sort={sort} order={order} onSort={onSort} />
              <th className="px-4 py-[10px] text-left text-[10px] font-semibold text-text-tertiary uppercase tracking-[0.5px] bg-bg-input border-b border-border-subtle">
                {t('filters.institution')}
              </th>
              <th className="px-4 py-[10px] text-left text-[10px] font-semibold text-text-tertiary uppercase tracking-[0.5px] bg-bg-input border-b border-border-subtle">
                {t('table.source')}
              </th>
              <SortTh label={t('table.votes')} column="vote_tally" sort={sort} order={order} onSort={onSort} />
              <SortTh label={t('common.created')} column="created_at" sort={sort} order={order} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-text-secondary text-[12px]">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-primary-red border-t-transparent rounded-full animate-spin" />
                    {t('table.loading')}
                  </div>
                </td>
              </tr>
            )}
            {!loading && projects.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-text-secondary text-[12px]">
                  {t('table.empty')}
                </td>
              </tr>
            )}
            {!loading &&
              projects.map(project => (
                <tr
                  key={project.id}
                  className="border-b border-border-subtle/50 hover:bg-[rgba(34,34,58,0.5)] cursor-pointer transition-colors duration-150"
                  onClick={() => onRowClick(project)}
                >
                  <td className="px-4 py-3 max-w-xs">
                    <span className="font-medium text-[12px] text-text-primary line-clamp-2 leading-snug">
                      {project.title}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <DomainBadge domain={project.domain} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <PipelineStageBadge stage={project.pipeline_stage} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <ScoreDisplay score={project.score_composite} />
                  </td>
                  <td className="px-4 py-3 text-[12px] text-text-secondary max-w-[180px] truncate">
                    {project.institution?.name ?? '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-[12px] text-text-secondary">
                    {project.source_class ?? '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <VoteTally voteTally={project.vote_tally} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-[12px] text-text-secondary">
                    {new Date(project.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {!loading && pagination.total > 0 && (
        <PaginationBar pagination={pagination} onPageChange={onPageChange} />
      )}
    </div>
  )
}
